// hooks/useAppInitialization.ts
import { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useSession } from '@descope/react-sdk';
import {
  defaultTargetOrganizationId,
  isOwnIdentitySelection,
  readIdentitySelection,
  resolveRestorableSelection,
  restorableOrganizationId,
  userInOrganization,
  useApiClientsOrNull,
  usePolledResource,
  type IdentitySelection,
} from 'design-system/my-lib';
import { i18n } from 'localization';
import { fetchMetaspaces } from '@center-pwa/reducers/metaspacesReducer';
import {
  setUserId,
  setUserLocale,
  setTokenClaims,
  fetchIdentity,
  setUserSphere,
} from '@center-pwa/reducers/userProfileReducer';
import { setActiveOrganizationId, useImpersonation } from '@center-technologies/shared-api-clients';
import { getSphereById } from '@center-platform/spheres/sphere';
import { globalSphereCache } from '@center-platform/spheres/cache';
import { SchemaNode, getLocalizations } from '@center-platform/spheres/identity';
import type { AppDispatch, RootState } from '@center-pwa/store/store';
import type { EmbeddedSagaProgress } from 'types/sagaProgress';
import {
  membershipFailureFromCode,
  organizationMembershipFailure,
  type OrganizationMembershipFailure,
} from '@center-pwa/utils/organizationMembership';

export type AppInitializationPhase =
  | 'idle'
  | 'authenticating'
  | 'bootstrapping'
  | 'no-organization'
  | 'provisioning-organization'
  | 'loading-profile'
  | 'loading-organization'
  | 'loading-localizations'
  | 'ready'
  | 'not-in-environment'
  | 'error';

/**
 * The body of `GET /organizations/provisioning-status` (organization-service). `progress` is
 * `null` when organization-service could not reach graph-service for the detail (D3) — the coarse
 * `status` still works then, and is what decides readiness.
 */
export interface OrganizationProvisioningStatus {
  status: 'deploying' | 'ready' | 'failed';
  ready?: boolean;
  deploymentId?: string | null;
  error?: string | null;
  progress?: EmbeddedSagaProgress | null;
}

/**
 * How many `provisioning-status` polls in a row may fail before the screen stops saying "setup is
 * still running" and tells the user it cannot reach the organization service (spec § UI States).
 */
export const PROVISIONING_UNREACHABLE_AFTER_FAILURES = 3;

const isSettledProvisioningStatus = (body: OrganizationProvisioningStatus): boolean =>
  body.status !== 'deploying';

/**
 * Where a signed-in person with no organization stands on the waitlist, as `/auth/bootstrap`
 * reports it. `approved` means an admin approved the request but the session does not yet carry
 * the organization — bootstrapping again picks it up, since membership is read from the graph.
 */
export type WaitlistStatus = 'pending' | 'approved' | 'rejected';

interface AppInitializationState {
  phase: AppInitializationPhase;
  error: string | null;
  warning: string | null;
  completedSteps: Set<string>;
  /** Set with the `no-organization` phase; `null` when bootstrap reported no waitlist entry. */
  waitlistStatus: WaitlistStatus | null;
  /** True when the waitlist entry is approved but the person's email is not yet verified. */
  waitlistVerificationRequired: boolean;
  /** The email `/auth/bootstrap` reported for the identity, kept for the `no-organization` screen. */
  identityEmail: string | null;
  /** True from `checkAgain()` until its bootstrap settles, so the gate screen can stay mounted. */
  checkingAgain: boolean;
  /** True when `error` is one trying again can fix (membership could not be checked just now). */
  errorRetryable: boolean;
  /**
   * With the `not-in-environment` phase: the organizations this person belongs to here, other
   * than the one the session could not use. Empty when there is nowhere else to go.
   */
  otherOrganizationIds: string[];
}

/**
 * Custom hook for handling the complete application initialization flow.
 * Manages authentication, user profile loading, organization data, and app state setup.
 */
export function useAppInitialization() {
  const { isSessionLoading, isAuthenticated, claims } = useSession();
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const apiClients = useApiClientsOrNull();

  // Internal state for tracking initialization progress
  const [state, setState] = useState<AppInitializationState>({
    phase: 'idle',
    error: null,
    warning: null,
    completedSteps: new Set(),
    waitlistStatus: null,
    waitlistVerificationRequired: false,
    identityEmail: null,
    checkingAgain: false,
    errorRetryable: false,
    otherOrganizationIds: [],
  });

  // Track attempted actions to prevent duplicate API calls
  const attemptedActions = useRef(new Set<string>());
  // The metaspaces fetch is started in the profile step and awaited in the
  // organization step; the promise crosses the two effects through this ref.
  const metaspacesPromiseRef = useRef<Promise<unknown> | null>(null);
  // /auth/bootstrap is called exactly once, in the bootstrapping step; its
  // identity.preferredLanguage is cached here so the profile step's language
  // detection doesn't call it again.
  const bootstrapIdentityLanguageRef = useRef<string | null>(null);
  /**
   * The identity `/auth/bootstrap` verified and returned. This — not a token claim —
   * is where the Descope path gets its identity id: `useSession().claims` is the
   * server's custom/common-claims object and carries no `sub`, so decoding it as if
   * it were the session JWT yielded `identityId: undefined` and every identity read
   * went to `/identities/undefined/…`.
   */
  const bootstrapIdentityRef = useRef<{ id: string; name: string } | null>(null);
  /**
   * The organizations `/auth/bootstrap` says the session belongs to — the server's
   * membership list, which is what a restored identity choice is checked against.
   */
  const memberOrganizationIdsRef = useRef<string[]>([]);
  /**
   * The identity-picker choice remembered on this identity (server-side, in its preferences),
   * when the bootstrapping step restored its organization (or the identity itself). The userId
   * step re-validates it against the loaded identity before acting as it.
   */
  const restoredSelectionRef = useRef<IdentitySelection | null>(null);
  /**
   * The organization the bootstrapping step put the session in (its Descope tenant), or null
   * when the person acts as their own identity. The userId step acts as the identity's user in
   * exactly this organization, so the user and the tenant never come from two organizations.
   */
  const activeOrganizationIdRef = useRef<string | null>(null);
  /**
   * True once a `organization_not_in_world` answer has re-run bootstrap: the session's token may
   * have named an organization from before the bootstrap filtered its list, so one fresh bootstrap
   * is worth it; a second answer means this organization really is not here.
   */
  const rebootstrappedForWorldRef = useRef(false);
  /** The organization the person chose on the not-in-environment screen, preferred by the next bootstrap. */
  const chosenOrganizationIdRef = useRef<string | null>(null);
  // The organization the bootstrapping step found not yet `ready`, carried into
  // the provisioning-organization poll below.
  const provisioningOrgIdRef = useRef<string | null>(null);
  // What the bootstrapping step's own `provisioning-status` check answered when it routed into
  // `provisioning-organization`; the poll's newer answers supersede it.
  const [bootstrapProvisioningStatus, setBootstrapProvisioningStatus] =
    useState<OrganizationProvisioningStatus | null>(null);
  // The auto-provisioned organization's display name, fetched once its foundation
  // approach finishes so the ready confirmation can name it (XP1 — a clear
  // statement, not a silent redirect). Not set on the "already ready" fast path
  // (see the bootstrapping step's own status check) — that path is a returning
  // user who never watched the deploy, so there is nothing new to confirm.
  const [provisioningOrgName, setProvisioningOrgName] = useState<string | null>(null);

  // Redux selectors - must be called at the top level
  const userProfile = useSelector((s: RootState) => s.userProfile);
  const metaspacesState = useSelector((s: RootState) => s.metaspaces);

  // Impersonation context
  const { isImpersonating, impersonationDetails } = useImpersonation();

  // ------- DEBUG HELPERS -------
  const logInit = (...args: any[]) => console.debug('[init]', ...args);
  const logApi = (...args: any[]) => console.debug('[api]', ...args);
  const logImp = (...args: any[]) => console.debug('[imp]', ...args);
  const logWarn = (...args: any[]) => console.warn('[warn]', ...args);
  const logErr = (...args: any[]) => console.error('[err]', ...args);

  /**
   * Helper function to display tree structure in console for debugging
   */
  const displayTreeStructure = useCallback(
    (nodes: any[], depth = 0, isLast = true) => {
      const indent = '  '.repeat(depth);
      let connector = '';
      if (depth !== 0) connector = isLast ? '└─ ' : '├─ ';

      nodes.forEach((node, index) => {
        const isLastChild = index === nodes.length - 1;
        if (node.children && node.children.length > 0) {
          displayTreeStructure(node.children, depth + 1, isLastChild);
        }
      });
    },
    []
  );

  // Helper to pretty-print a tree of SchemaNode
  const displayTree = (nodes: SchemaNode[], depth = 0, isLast = true) => {
    const indent = '  '.repeat(depth);
    let connector = '';
    if (depth !== 0) connector = isLast ? '└─ ' : '├─ ';

    nodes.forEach((node, idx) => {
      const lastChild = idx === nodes.length - 1;
      if (node.children?.length) {
        displayTree(node.children, depth + 1, lastChild);
      }
    });
  };

  // Computed state for determining when we can proceed with initialization
  const isDescopeReady = !isSessionLoading;
  const canProceed = isDescopeReady && isAuthenticated && apiClients;

  /**
   * Helper function to update the internal state
   */
  const updateState = useCallback(
    (updates: Partial<AppInitializationState>) => {
      setState((prev) => ({ ...prev, ...updates }));
    },
    []
  );

  /**
   * Helper function to mark a step as completed
   */
  const markStepCompleted = useCallback((step: string) => {
    setState((prev) => ({
      ...prev,
      completedSteps: new Set([...prev.completedSteps, step]),
    }));
  }, []);

  /**
   * Handle user login by routing to the in-app Descope login page
   */
  const handleLogin = useCallback(() => {
    updateState({ phase: 'authenticating' });
    logInit('Navigating to /login…');
    navigate('/login', { replace: true });
  }, [updateState, navigate]);

  /**
   * Step 1: Handle authentication
   */
  useEffect(() => {
    logInit('Descope session state', { isDescopeReady, isAuthenticated });

    if (!isDescopeReady) return;

    if (!isAuthenticated) {
      logInit('Not authenticated → redirecting to login');
      handleLogin();
    } else if (state.phase === 'idle' || state.phase === 'authenticating') {
      logInit('Authenticated → move to bootstrapping');
      updateState({ phase: 'bootstrapping' });
      markStepCompleted('authentication');
    }
  }, [
    isDescopeReady,
    isAuthenticated,
    handleLogin,
    state.phase,
    updateState,
    markStepCompleted,
  ]);

  /**
   * Helper: Map language code to supported locale
   */
  const mapToSupportedLocale = useCallback((languageCode: string): string | null => {
    if (languageCode.startsWith('en')) return 'en-US';
    if (languageCode.startsWith('fr')) return 'fr-FR';
    return null;
  }, []);

  /** Starts initialisation again from the bootstrapping step: every step after authentication runs afresh. */
  const restartFromBootstrap = useCallback(
    () => {
      attemptedActions.current.clear();
      metaspacesPromiseRef.current = null;
      setState((prev) => ({
        ...prev,
        phase: 'bootstrapping',
        error: null,
        errorRetryable: false,
        otherOrganizationIds: [],
        completedSteps: new Set(['authentication']),
      }));
    },
    []
  );

  /**
   * One place for the two membership answers, wherever initialisation meets them:
   * `membership-unavailable` is an error the person can retry; `not-in-world` re-runs bootstrap
   * once, then shows the not-in-environment state with the organizations they do belong to here.
   */
  const handleMembershipFailure = useCallback(
    (failure: OrganizationMembershipFailure) => {
      if (failure === 'membership-unavailable') {
        updateState({ phase: 'error', error: i18n.t('appInit.membershipUnavailable.description'), errorRetryable: true });
        return;
      }
      if (!rebootstrappedForWorldRef.current) {
        rebootstrappedForWorldRef.current = true;
        logWarn('The session named an organization that is not in this environment; bootstrapping once more');
        restartFromBootstrap();
        return;
      }
      const unusable = activeOrganizationIdRef.current;
      updateState({
        phase: 'not-in-environment',
        otherOrganizationIds: memberOrganizationIdsRef.current.filter((id) => id !== unusable),
      });
    },
    [restartFromBootstrap, updateState]
  );

  /**
   * Step 1.5: Just-in-time provisioning + active organization selection.
   *
   * Calls `/auth/bootstrap` exactly once per session. A user with no
   * organizations lands in the `no-organization` empty state — there is
   * nothing further to load. Otherwise the session is put in the context the
   * person last chose in the identity picker, when that choice is still valid:
   * their own identity (no organization header), or the organization of the
   * center_user they chose, if the identity is still a member of it. With no
   * valid saved choice, the identity's default organization is used. The active
   * organization is client state (`setActiveOrganizationId`), never a session claim.
   */
  useEffect(() => {
    if (state.phase !== 'bootstrapping' || !apiClients) return;
    if (attemptedActions.current.has('bootstrap')) return;
    attemptedActions.current.add('bootstrap');

    const runBootstrap = async () => {
      try {
        const response = await apiClients.organizationClient.post('/auth/bootstrap');
        const body = response.data || response;
        const organizationIds: string[] = body.organizationIds ?? [];
        const preferredLanguage = body.identity?.preferredLanguage;
        bootstrapIdentityLanguageRef.current = preferredLanguage
          ? mapToSupportedLocale(preferredLanguage)
          : null;
        if (!body.identity?.id) {
          throw new Error('Bootstrap response has no identity id');
        }
        bootstrapIdentityRef.current = { id: body.identity.id, name: body.identity.name ?? '' };
        memberOrganizationIdsRef.current = organizationIds;

        logInit('Bootstrap complete', { organizationIds });

        if (organizationIds.length === 0) {
          updateState({
            phase: 'no-organization',
            waitlistStatus: body.waitlist?.status ?? null,
            waitlistVerificationRequired: body.waitlist?.verificationRequired === true,
            identityEmail: body.identity?.email ?? null,
            checkingAgain: false,
          });
          return;
        }

        // The picker choice remembered on the identity (graph-service). A read failure costs
        // only the restore, never the sign-in: the default organization is used instead.
        let savedSelection: IdentitySelection | null = null;
        try {
          savedSelection = await readIdentitySelection(apiClients.graphClient, body.identity.id);
        } catch (selectionError) {
          logWarn('Could not read the remembered identity selection; using the default', selectionError);
        }

        if (isOwnIdentitySelection(savedSelection)) {
          // Last time the person chose to act as themselves: no organization header is
          // sent, and there is no organization to check provisioning for.
          try {
            restoredSelectionRef.current = savedSelection;
            activeOrganizationIdRef.current = null;
            setActiveOrganizationId(null);
            updateState({ phase: 'loading-profile' });
            return;
          } catch (restoreError) {
            logWarn('Could not restore the own-identity choice; using the default organization', restoreError);
          }
        }

        // With no choice to restore, the organization of the identity's default user
        // (bootstrap's `defaultOrganizationId`) — the userId step then acts as that user.
        const savedOrgId = restorableOrganizationId(savedSelection, organizationIds);
        // An organization chosen on the not-in-environment screen wins over everything else.
        const chosenOrgId =
          chosenOrganizationIdRef.current && organizationIds.includes(chosenOrganizationIdRef.current)
            ? chosenOrganizationIdRef.current
            : null;
        const targetOrgId =
          chosenOrgId ?? savedOrgId ?? defaultTargetOrganizationId(body.defaultOrganizationId, null, organizationIds);
        if (!targetOrgId) {
          throw new Error('Bootstrap reported organizations but none could be selected');
        }

        logInit(savedOrgId ? 'Restoring the saved organization' : 'Entering the default organization', { targetOrgId });
        setActiveOrganizationId(targetOrgId);
        activeOrganizationIdRef.current = targetOrgId;
        restoredSelectionRef.current = !chosenOrgId && savedOrgId ? savedSelection : null;

        // A user with organizations may have one whose foundation approach is
        // still deploying (just auto-provisioned at sign-up, or a retry in
        // flight) — routing them into it now shows a structureless org. Check
        // once here; the polling effect below takes over if it isn't ready yet.
        try {
          const statusResponse = await apiClients.organizationClient.get(
            '/organizations/provisioning-status'
          );
          const statusBody: OrganizationProvisioningStatus = statusResponse.data ?? statusResponse;

          if (statusBody.status === 'deploying' || statusBody.status === 'failed') {
            if (statusBody.status === 'deploying') {
              logInit('Organization still provisioning — waiting', { targetOrgId });
            } else {
              logErr('Organization provisioning failed', { targetOrgId, error: statusBody.error });
            }
            provisioningOrgIdRef.current = targetOrgId;
            setBootstrapProvisioningStatus(statusBody);
            updateState({ phase: 'provisioning-organization' });
            return;
          }
        } catch (statusError) {
          // provisioning-status answers 404 organization_not_in_world for an organization this
          // environment does not have; anything else only costs the readiness check.
          if (organizationMembershipFailure(statusError) === 'not-in-world') {
            handleMembershipFailure('not-in-world');
            return;
          }
          logWarn('Failed to check organization provisioning status:', statusError);
        }

        updateState({ phase: 'loading-profile' });
      } catch (error) {
        logErr('Bootstrap failed:', error);
        const failure = organizationMembershipFailure(error);
        if (failure) {
          handleMembershipFailure(failure);
          return;
        }
        updateState({
          phase: 'error',
          error: i18n.t('appInit.userProfileLoadFailed'),
        });
      }
    };

    void runBootstrap();
  }, [
    state.phase,
    apiClients,
    isAuthenticated,
    updateState,
    mapToSupportedLocale,
    handleMembershipFailure,
  ]);

  /** Tries initialisation again after an error trying again can fix (`errorRetryable`). */
  const retryInitialization = useCallback(() => {
    restartFromBootstrap();
  }, [restartFromBootstrap]);

  /**
   * From the not-in-environment screen: enters the next organization this person belongs to
   * here. The bootstrapping step puts the session in it.
   */
  const chooseAnotherOrganization = useCallback(() => {
    const next = state.otherOrganizationIds[0];
    if (!next) return;
    chosenOrganizationIdRef.current = next;
    restartFromBootstrap();
  }, [state.otherOrganizationIds, restartFromBootstrap]);

  /**
   * Re-checks a person who has no organization yet (the waitlist gate's *Check again* /
   * *Continue*): runs the bootstrapping step again from the start. Membership comes from the
   * graph, not the session, so no session renewal is needed.
   */
  const checkAgain = useCallback(() => {
    if (attemptedActions.current.has('check-again')) return;
    attemptedActions.current.add('check-again');
    setState((prev) => ({ ...prev, checkingAgain: true }));
    attemptedActions.current.delete('bootstrap');
    attemptedActions.current.delete('check-again');
    setState((prev) => ({ ...prev, phase: 'bootstrapping' }));
  }, []);

  /**
   * Step 1.6: While `provisioning-organization` and the organization is still `deploying`, poll
   * `provisioning-status` through the shared `usePolledResource` engine (2 s, 10 s while the tab
   * is hidden, exponential back-off on failure, last good answer kept) until it is `ready` — the
   * screen then holds on a named confirmation (`continueAfterProvisioning` is what actually
   * advances to `loading-profile`) — or `failed`, where the screen shows the failure and *Sign
   * out*, not Retry, since a saga that failed once needs investigation, not a client-side replay.
   */
  const fetchProvisioningStatus = useCallback(async (): Promise<OrganizationProvisioningStatus> => {
    if (!apiClients) throw new Error('API clients are not ready');
    const response = await apiClients.organizationClient.get('/organizations/provisioning-status');
    return response.data ?? response;
  }, [apiClients]);

  const provisioningPoll = usePolledResource(fetchProvisioningStatus, {
    active:
      state.phase === 'provisioning-organization' &&
      bootstrapProvisioningStatus?.status === 'deploying' &&
      !!apiClients,
    isTerminal: isSettledProvisioningStatus,
  });

  const provisioningSnapshot = provisioningPoll.data ?? bootstrapProvisioningStatus;
  const provisioningStatus = provisioningSnapshot?.status ?? null;
  const provisioningProgress = provisioningSnapshot?.progress ?? null;
  // The saga run id is the support reference — the same id the progress molecule prints — never
  // `deploymentId`, which would hand support a second id for the same incident.
  const provisioningRunId = provisioningProgress?.runId ?? null;
  // A 404 is an answer, not an outage: the organization is not in this environment. Trying again
  // cannot change it, so it is reported on its own rather than as unreachable.
  const provisioningNotFound = provisioningPoll.deniedReason === 'not_found';
  const provisioningUnreachable =
    !provisioningNotFound &&
    (provisioningPoll.deniedReason !== null ||
      provisioningPoll.consecutiveFailures >= PROVISIONING_UNREACHABLE_AFTER_FAILURES);

  useEffect(() => {
    if (provisioningPoll.error) {
      logWarn('Provisioning status poll failed:', provisioningPoll.error);
    }
  }, [provisioningPoll.error]);

  /**
   * Fetches the auto-provisioned organization's display name once provisioning
   * reports ready, so the ready confirmation can name it.
   */
  useEffect(() => {
    if (provisioningStatus !== 'ready') return;
    const organizationId = provisioningOrgIdRef.current;
    if (!organizationId || !apiClients) return;

    let cancelled = false;
    globalSphereCache
      .getSphereData(organizationId, apiClients.graphClient)
      .then((cached) => {
        if (!cancelled) {
          setProvisioningOrgName(cached.info.sphere?.properties?.name ?? null);
        }
      })
      .catch((nameError) => {
        logWarn('Failed to load organization name for the ready confirmation:', nameError);
      });

    return () => {
      cancelled = true;
    };
  }, [provisioningStatus, apiClients]);

  /**
   * Advances past the `provisioning-organization` ready confirmation once the user has
   * seen it — the poll above deliberately does not do this itself (see its doc comment).
   */
  const continueAfterProvisioning = useCallback(() => {
    updateState({ phase: 'loading-profile' });
  }, [updateState]);

  /**
   * Helper: Detect language with hierarchy (bootstrap preferredLanguage → browser → default).
   * `preferences.language` is no longer consulted — `preferredLanguage` on the
   * identity vertex is the system of record post-Phase-3 (see
   * specs/descope-auth-surface.md Phase 3.3).
   */
  const detectLanguagePreference = useCallback((): string => {
    // 1. The identity's preferred_language, seeded from the Descope session's `locale` claim and
    //    read out of the bootstrapping step's single `/auth/bootstrap` call rather than fetched again.
    const identityLanguage = bootstrapIdentityLanguageRef.current;
    if (identityLanguage) {
      logInit('Using identity language:', identityLanguage);
      return identityLanguage;
    }

    // 2. Check browser language
    const browserLanguage = mapToSupportedLocale(navigator.language);
    if (browserLanguage) {
      logInit('Using browser language:', browserLanguage);
      return browserLanguage;
    }

    // 3. Default fallback
    logInit('Using default language: en-US');
    return 'en-US';
  }, [mapToSupportedLocale]);

  /**
   * Step 2: Load user profile and identity
   */
  useEffect(() => {
    if (state.phase !== 'loading-profile' || !canProceed) return;
    if (!apiClients) return;
    if (attemptedActions.current.has('profile')) return;

    attemptedActions.current.add('profile');

    const loadProfile = async () => {
      try {
        // The identity is the one `/auth/bootstrap` verified. The session's `claims`
        // object is not the decoded JWT and has no `sub`; it is consulted only for the
        // display-name fallback.
        const bootstrapIdentity = bootstrapIdentityRef.current;
        if (!bootstrapIdentity) {
          throw new Error('Profile load started before bootstrap resolved the identity');
        }
        const effectiveIdentityId = bootstrapIdentity.id;
        const displayName = bootstrapIdentity.name || (claims?.name as string | undefined) || '';
        logInit('Using bootstrap identity', { sub: effectiveIdentityId });

        dispatch(setTokenClaims({
          identityId: effectiveIdentityId,
          displayName,
        }));

        // Metaspaces need nothing from the identity, so they start here and
        // overlap the identity fetch instead of queuing behind it. The
        // organization step awaits the same promise.
        if (!attemptedActions.current.has('metaspaces')) {
          attemptedActions.current.add('metaspaces');
          metaspacesPromiseRef.current = dispatch(
            fetchMetaspaces({ apiClient: apiClients.schemaClient })
          );
        }

        // The identity and the saved language preference are independent reads
        // keyed by the same id: run them together rather than one after the other.
        const [, locale] = await Promise.all([
          dispatch(
            fetchIdentity({
              identityId: effectiveIdentityId,
              apiClient: apiClients.graphClient,
            })
          ),
          detectLanguagePreference(),
        ]);
        logInit('Identity fetch completed');

        // Apply language to i18n and Redux
        i18n.changeLanguage(locale);
        dispatch(setUserLocale({ locale }));

        logInit('Language preference applied:', locale);
        markStepCompleted('profile');
      } catch (error) {
        logErr('Profile loading failed:', error);
        updateState({
          phase: 'error',
          error: i18n.t('appInit.userProfileLoadFailed'),
        });
      }
    };

    logInit('Loading profile…');
    loadProfile();
  }, [
    state.phase,
    canProceed,
    claims,
    dispatch,
    apiClients,
    markStepCompleted,
    updateState,
    detectLanguagePreference,
  ]);

  /**
   * Step 3: Extract userId and transition
   *
   * Waits for the profile step so the identity it reads is the one this sign-in fetched: the
   * store outlives a session, and a `succeeded` identity left by the previous one would otherwise
   * complete this step before the profile phase began and strand the transition below.
   */
  useEffect(() => {
    if (!state.completedSteps.has('profile')) return;
    if (
      !userProfile.identity ||
      userProfile.fetchIdentityStatus !== 'succeeded'
    )
      return;
    if (state.completedSteps.has('userId')) return;

    const { identity, users } = userProfile.identity;
    const defaultUserId = identity.defaultUserId?.[0];
    // The saved picker choice wins when it still names the identity itself or one of its
    // users in the organization the bootstrapping step restored; otherwise the default.
    const restoredUserId = resolveRestorableSelection(
      restoredSelectionRef.current,
      userProfile.identity,
      memberOrganizationIdsRef.current
    )?.userId;
    // Otherwise the identity's user in the organization the session's tenant names — its
    // default user when that user is there. A user from another organization would pair this
    // session's tenant with someone who is not a member of it.
    const activeOrganizationId = activeOrganizationIdRef.current;
    const organizationUserId = activeOrganizationId
      ? userInOrganization(userProfile.identity, activeOrganizationId, defaultUserId)
      : null;
    if (!restoredUserId && activeOrganizationId && !organizationUserId) {
      logErr('The identity has no user in the organization the session entered; acting as the identity', {
        identityId: identity.id,
        activeOrganizationId,
        userCount: users.length,
      });
    }
    const realId = restoredUserId ?? organizationUserId ?? identity.id;

    logInit('Resolved user IDs', {
      identityId: identity.id,
      activeOrganizationId,
      restoredUserId,
      defaultUserId,
      realIdUsed: realId,
    });

    dispatch(setUserId({ userId: realId }));
    markStepCompleted('userId');

    if (state.phase === 'loading-profile') {
      updateState({ phase: 'loading-organization' });
    }
  }, [
    userProfile.identity,
    userProfile.fetchIdentityStatus,
    dispatch,
    state.completedSteps,
    state.phase,
    markStepCompleted,
    updateState,
  ]);

  /**
   * Load the schema + use_case localisation bundles for the org. Never rejects:
   * a failed bundle is logged and the step still completes so the app can run
   * on the built-in strings. Runs at most once.
   */
  const loadLocalizations = useCallback(
    async (orgId: string | undefined): Promise<void> => {
      if (!apiClients) return;
      if (attemptedActions.current.has('localisations')) return;
      attemptedActions.current.add('localisations');

      const locale = userProfile.locale ?? i18n.language;
      const namespaces: ReadonlyArray<'schema' | 'use_case'> = ['schema', 'use_case'];
      logApi('Fetching localizations…', { orgId, locale, namespaces });

      const loadNamespace = async (ns: (typeof namespaces)[number]) => {
        const { data } = await getLocalizations(apiClients.schemaClient, {
          locale,
          namespace: ns,
          bundle: true,
        });
        i18n.addResourceBundle(
          locale,
          ns,
          data,
          /* deep      */ true,
          /* overwrite */ true
        );
      };

      try {
        await Promise.all(namespaces.map(loadNamespace));
        logApi('Localizations loaded ✅');
      } catch (err) {
        logWarn('Localisations load failed:', err);
      } finally {
        markStepCompleted('localizations');
      }
    },
    [apiClients, userProfile.locale, markStepCompleted]
  );

  /**
   * Step 4: Load organization data (sphere + metaspaces + localisations + impersonation)
   */
  useEffect(() => {
    if (state.phase !== 'loading-organization' || !canProceed) return;
    const userId = userProfile.userId; // narrow via local const
    if (!userId) return;               // guard out null
    if (!apiClients) return;
    if (attemptedActions.current.has('organization')) return;

    attemptedActions.current.add('organization');

    const loadOrganizationData = async () => {
      try {
        // Load sphere and metaspaces separately to handle errors independently
        logApi('Loading org data…', { userId });
        const spherePromise = getSphereById(
          apiClients.graphClient,
          userId
        );
        // Normally already in flight since the profile step; started here only
        // if that step could not (no schema client yet).
        const metaspacesPromise =
          metaspacesPromiseRef.current ??
          dispatch(fetchMetaspaces({ apiClient: apiClients.schemaClient }));

        let loadedOrgId: string | undefined;

        // Handle sphere loading with error tolerance
        try {
          const sphereResult = await spherePromise;
          if (sphereResult.success) {
            const sphere = sphereResult.data.sphere;
            loadedOrgId = sphere?.properties?.sphereId;
            dispatch(setUserSphere({ sphere }));
            logApi('Sphere loaded', { orgId: loadedOrgId });
          }
        } catch (sphereError) {
          logWarn('Failed to load user sphere:', sphereError);
          // Don't fail the entire initialization for sphere errors
          loadedOrgId = userProfile.userSphere?.properties?.sphereId;
        }

        // Handle metaspaces loading with error tolerance
        try {
          await metaspacesPromise;
          logApi('Metaspaces loaded');
        } catch (metaspacesError) {
          logWarn('Failed to load metaspaces:', metaspacesError);
        }

        const orgId =
          loadedOrgId || userProfile.userSphere?.properties?.sphereId;

        // Self-impersonation is gone with Descope: the active organization (set during
        // bootstrap) names the organization the user acts in, and `userProfile.userId`
        // names the center_user.
        await loadLocalizations(orgId);

        markStepCompleted('organization');
        updateState({ phase: 'loading-localizations' });
      } catch (error) {
        logErr('Organization data loading failed:', error);
        updateState({
          phase: 'error',
          error: i18n.t('appInit.organizationLoadFailed'),
        });
      }
    };

    loadOrganizationData();
  }, [
    state.phase,
    userProfile.userId,
    canProceed,
    apiClients,
    dispatch,
    markStepCompleted,
    updateState,
    userProfile.identity,
    userProfile.userSphere,
    loadLocalizations,
  ]);

  /* ------------------------------------------------------------------
   * Keep userSphere in sync with the effective (impersonated) identity.
   *
   * userSphere is loaded once at init for the logged-in user, but an identity
   * is multi-org (one center_user sphere per org) and impersonation switches
   * the effective user/org. Without re-resolving, userSphere stays pinned to
   * the login org, so every consumer (metaspaces, profile, org-scoped fetches)
   * reads the wrong org while impersonating. Re-fetch the sphere whenever the
   * impersonated target changes; the graphClient sends the impersonation token,
   * so this resolves the impersonated user's sphere in the active org.
   * ----------------------------------------------------------------- */
  useEffect(() => {
    const targetUserSphereId = impersonationDetails?.targetUserId;
    if (!targetUserSphereId || !apiClients?.graphClient) return;
    // Already the effective sphere — nothing to do (also breaks the update loop).
    if (userProfile.userSphere?.id === targetUserSphereId) return;
    // The organization step loads the login user's own sphere; on a reload the
    // stored impersonation session names that same user before the step has run,
    // and re-fetching here would only race it. Only a *different* target needs this.
    if (userProfile.userId === targetUserSphereId) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await getSphereById(apiClients.graphClient, targetUserSphereId);
        if (!cancelled && res.success && res.data?.sphere) {
          dispatch(setUserSphere({ sphere: res.data.sphere }));
        }
      } catch (err) {
        logWarn('Failed to refresh userSphere for impersonated identity:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [impersonationDetails?.targetUserId, userProfile.userSphere?.id, userProfile.userId, apiClients, dispatch]);

  /**
   * Step 6: Check if we're ready
   */
  useEffect(() => {
    if (state.phase !== 'loading-localizations') return;

    const hasRequiredData =
      state.completedSteps.has('authentication') &&
      state.completedSteps.has('profile') &&
      state.completedSteps.has('userId') &&
      state.completedSteps.has('organization') &&
      state.completedSteps.has('localizations');

    if (hasRequiredData) {
      logInit('Initialization complete ✅');
      updateState({ phase: 'ready' });
    } else {
      logInit('Waiting for remaining steps…', {
        completed: Array.from(state.completedSteps),
      });
    }
  }, [state.phase, state.completedSteps, updateState]);

  /**
   * Handle errors from Redux actions
   */
  useEffect(() => {
    if (userProfile.fetchIdentityStatus === 'failed') {
      logErr('Identity fetch failed:', userProfile.fetchIdentityError);
      const failure = membershipFailureFromCode(userProfile.fetchIdentityErrorCode);
      if (failure) {
        handleMembershipFailure(failure);
        return;
      }
      updateState({
        phase: 'error',
        error:
          userProfile.fetchIdentityError ?? 'Failed to fetch user identity',
      });
    }
  }, [
    userProfile.fetchIdentityStatus,
    userProfile.fetchIdentityError,
    userProfile.fetchIdentityErrorCode,
    updateState,
    handleMembershipFailure,
  ]);

  // Return the current state and helper properties for components to use
  return {
    phase: state.phase,
    isReady: state.phase === 'ready',
    isLoading: [
      'authenticating',
      'bootstrapping',
      'loading-profile',
      'loading-organization',
      'loading-localizations',
    ].includes(state.phase),
    hasError: state.phase === 'error',
    errorRetryable: state.errorRetryable,
    retryInitialization,
    isNotInEnvironment: state.phase === 'not-in-environment',
    hasOtherOrganizations: state.otherOrganizationIds.length > 0,
    chooseAnotherOrganization,
    hasNoOrganization: state.phase === 'no-organization',
    waitlistStatus: state.waitlistStatus,
    waitlistVerificationRequired: state.waitlistVerificationRequired,
    identityEmail: state.identityEmail,
    isCheckingAgain:
      state.checkingAgain && (state.phase === 'no-organization' || state.phase === 'bootstrapping'),
    checkAgain,
    isProvisioningOrganization: state.phase === 'provisioning-organization',
    provisioningStatus,
    provisioningProgress,
    provisioningRunId,
    provisioningOrgName,
    provisioningPollError: provisioningPoll.error,
    provisioningUnreachable,
    provisioningNotFound,
    retryProvisioningStatus: provisioningPoll.refetch,
    continueAfterProvisioning,
    error: state.error,
    warning: state.warning,
    userId: userProfile.userId,
    completedSteps: Array.from(state.completedSteps),

    // Detailed status for debugging and monitoring
    debug: {
      descope: {
        isSessionLoading,
        isDescopeReady,
        isAuthenticated,
      },
      impersonation: {
        isImpersonating,
        triedAuto: attemptedActions.current.has('auto-impersonation'),
      },
      userProfileStatus: userProfile.fetchIdentityStatus,
      metaspacesStatus: metaspacesState.status,
      hasIdentity: !!userProfile.identity,
      hasUserId: !!userProfile.userId,
      hasOrganizationId: !!userProfile.userSphere?.properties?.sphereId,
    },
  };
}
